import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { servicesData, generateServiceSlug } from './servicesData';

// Debounce function
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isSticky, setIsSticky] = useState(false);
  const headerRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sticky header
  useEffect(() => {
    const handleScroll = debounce(() => {
      setIsSticky(window.scrollY > 100);
    }, 100);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleKeyDown = (e) => e.key === "Escape" && toggleMobileMenu();
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMobileMenuOpen]);

  const toggleDropdown = useCallback((dropdown) => {
    setActiveDropdown((prev) => (prev === dropdown ? null : dropdown));
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
    setActiveDropdown(null);
  }, []);

  // Animation variants
  const dropdownVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", damping: 25, stiffness: 300 },
    },
    exit: { opacity: 0, y: -10 },
  };

  const megaMenuVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: {
      opacity: 1,
      height: "auto",
      transition: { duration: 0.3, ease: "easeInOut" },
    },
    exit: {
      opacity: 0,
      height: 0,
      transition: { duration: 0.2, ease: "easeInOut" },
    },
  };
  const contactInfo = {
    phones: ["+91-9326897902", "+91-9322811574"],
    email: "info@environaturesolution.com",
    social: [
      { icon: "fa-brands fa-facebook", href: "#" },
      { icon: "fa-brands fa-linkedin", href: "#" },
      { icon: "fa-brands fa-instagram", href: "#" },
      { icon: "fa-brands fa-x-twitter", href: "#" },
      { icon: "fa-brands fa-youtube", href: "#" },
    ],
  };

  // Generate slug for each service item
 

  return (
  <div className="font-sans text-gray-800" ref={headerRef}>
    {/* Top Bar - Hidden on mobile */}
    <div
      className={`bg-green-600 text-black py-2 transition-all ${
        isSticky ? "hidden" : ""
      } hidden sm:block`}
    >
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-center gap-2">
          {/* Contact Info - Stacked on mobile */}
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
            <div className="flex items-center justify-center sm:justify-start w-full sm:w-auto">
              <i className="fas fa-phone-alt text-blue-300 mr-2 text-sm"></i>
              <div className="flex items-center gap-1 sm:gap-2">
                {contactInfo.phones.map((phone, index) => (
                  <React.Fragment key={phone}>
                    <a
                      href={`tel:${phone.replace(/-/g, "")}`}
                      className="text-xs sm:text-sm hover:text-blue-200 transition-colors whitespace-nowrap"
                    >
                      {phone}
                    </a>
                    {index < contactInfo.phones.length - 1 && (
                      <span className="text-xs sm:text-sm mx-1">|</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-center sm:justify-start w-full sm:w-auto">
              <i className="fas fa-envelope text-blue-300 mr-2 text-sm"></i>
              <a
                href={`mailto:${contactInfo.email}`}
                className="text-xs sm:text-sm hover:text-blue-200 transition-colors whitespace-nowrap truncate max-w-[180px] sm:max-w-none"
              >
                {contactInfo.email}
              </a>
            </div>
          </div>

          {/* Social Icons - Centered on mobile */}
          <div className="flex justify-center sm:justify-end w-full sm:w-auto">
            <div className="flex space-x-2 sm:space-x-3">
              {contactInfo.social.map((social) => (
                <a
                  key={social.icon}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-600 hover:bg-blue-500 flex items-center justify-center transition-colors"
                  aria-label={social.icon.split("-")[2]}
                >
                  <i className={`${social.icon} text-white text-xs sm:text-sm`}></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Main Navigation */}
    <nav
      className={`bg-white shadow-md transition-all duration-300 ${
        isSticky
          ? "fixed top-0 left-0 right-0 z-50 shadow-lg animate-fadeIn"
          : ""
      }`}
      aria-label="Main navigation"
    >
      <div className="container px-6">
        <div className="flex justify-between items-center py-2">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src="/logo2.png"
                alt="Company Logo"
                className="h-12"
              />
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-10">
            <Link to="/" className="nav-link">
              HOME
            </Link>
            <Link to="/about" className="nav-link">
              ABOUT US
            </Link>

            {/* Services Dropdown */}
            <div className="">
              <button
                onClick={() => toggleDropdown("services")}
                onMouseEnter={() => setActiveDropdown("services")}
                className="nav-link flex items-center"
                aria-expanded={activeDropdown === "services"}
              >
                SERVICES
                <i
                  className={`fas fa-chevron-down ml-2 text-xs transition-transform ${
                    activeDropdown === "services" ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              <AnimatePresence>
                {activeDropdown === "services" && (
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={megaMenuVariants}
                    className="absolute left-1/2 transform -translate-x-1/2 mt-5 w-screen max-w-6xl bg-gray-50 shadow-xl z-50 rounded-lg border border-gray-100"
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <div className="container mx-auto p-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        {servicesData.map((section, index) => (
                          <div key={index} className="group">
                            <h4 className="text-lg font-semibold text-black uppercase px-3 py-2 bg-green-500 mb-4">
                              {section.title}
                            </h4>
                            <ul className="space-y-3.5">
                              {section.items.map((item, itemIndex) => {
                                const slug = generateServiceSlug(item);
                                return (
                                  <li key={itemIndex}>
                                    <Link
                                      to={`/services#${slug}`}
                                      className="group flex items-start  text-black hover:text-green-800 transition pl-2 border-l-4 border-transparent hover:border-green-500 mb-2"
                                      onClick={() => setActiveDropdown(null)}
                                    >
                                      <i className="fas fa-check-circle text-green-500 mt-1 mr-3 text-sm"></i>
                                      <span>{item}</span>
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link to="/contact" className="nav-link">
              CONTACT US
            </Link>
            
          </div>

          {/* Mobile Menu Button */}
          {!isMobileMenuOpen && (
            <button
              className="lg:hidden p-2 text-gray-700 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300 rounded transition"
              onClick={toggleMobileMenu}
              aria-label="Open menu"
            >
              <i className="fa fa-bars text-2xl"></i>
            </button>
          )}
        </div>
      </div>
    </nav>

    {/* Mobile Menu Overlay */}
    <AnimatePresence>
      {isMobileMenuOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black"
            onClick={toggleMobileMenu}
          />
          
          {/* Mobile Menu Panel */}
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed inset-y-0 left-0 w-4/5 max-w-xs bg-gray-100 shadow-lg z-50 overflow-y-auto"
          >
            {/* Mobile Menu Header */}
            <div className="flex justify-between items-center px-4 py-4 border-b border-gray-300 bg-white">
              <Link to="/" onClick={toggleMobileMenu}>
                <img src="/logo2.png" alt="Company Logo" className="h-10" />
              </Link>
              <button
                onClick={toggleMobileMenu}
                className="text-gray-700 hover:text-red-500 transition"
              >
                <i className="fa fa-times text-2xl"></i>
              </button>
            </div>

            {/* Mobile Menu Content */}
            <div className="px-4 py-6">
              <ul className="space-y-1">
                <li>
                  <Link
                    to="/"
                    onClick={toggleMobileMenu}
                    className="block py-3 text-sm font-medium text-gray-900 border-b border-teal-600 hover:text-teal-600 transition"
                  >
                    Home
                  </Link>
                </li>

                <li>
                  <Link
                    to="/about"
                    onClick={toggleMobileMenu}
                    className="block py-3 text-sm font-medium text-gray-900 border-b border-teal-600 hover:text-teal-600 transition"
                  >
                    About
                  </Link>
                </li>

                {/* Mobile Services Dropdown */}
                <li className="border-b border-teal-600">
                  <button
                    onClick={() => toggleDropdown("mobile-services")}
                    className="w-full flex justify-between items-center py-3 text-sm font-medium text-gray-900 hover:text-teal-600 transition"
                  >
                    <span>Services</span>
                    <i
                      className={`fas fa-chevron-right text-sm transition-transform ${
                        activeDropdown === "mobile-services" ? "rotate-90" : ""
                      }`}
                    ></i>
                  </button>

                  {activeDropdown === "mobile-services" && (
                    <div className="pl-4 mt-2 space-y-2">
                      {servicesData.map((section, index) => (
                        <div key={index}>
                          <h5 className="text-sm font-semibold text-gray-800 mb-1">
                            {section.title}
                          </h5>
                          <ul className="space-y-1">
                            {section.items.map((item, itemIndex) => {
                              const slug = generateServiceSlug(item);
                              return (
                                <li key={itemIndex}>
                                  <Link
                                    to={`/services#${slug}`}
                                    onClick={() => {
                                      setActiveDropdown(null);
                                      toggleMobileMenu();
                                    }}
                                    className="flex items-center text-sm text-gray-700 hover:text-teal-600 transition"
                                  >
                                    <i className="fas fa-check-circle text-teal-500 mr-2 text-xs"></i>
                                    {item}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </li>

                <li>
                  <Link
                    to="/contact"
                    onClick={toggleMobileMenu}
                    className="block py-3 text-sm font-medium text-gray-900 border-b border-teal-600 hover:text-teal-600 transition"
                  >
                    Contact
                  </Link>
                </li>
              </ul>

              {/* Contact Information Section */}
              <div className="mt-12 pt-6 border-t border-gray-300 text-sm text-gray-800">
                <h4 className="text-sm font-semibold mb-4 text-gray-900">Contact Us</h4>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center">
                      <i className="fa fa-phone text-xs"></i>
                    </span>
                    <div className="space-y-1">
                      {contactInfo.phones.map((phone) => (
                        <a
                          key={phone}
                          href={`tel:${phone.replace(/-/g, "")}`}
                          className="hover:text-teal-600 transition block"
                        >
                          {phone}
                        </a>
                      ))}
                    </div>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center">
                      <i className="fa fa-envelope text-xs"></i>
                    </span>
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="hover:text-teal-600 transition"
                    >
                      {contactInfo.email}
                    </a>
                  </li>
                </ul>

                {/* Social Media Links */}
                <div className="mt-5">
                  <h4 className="text-sm font-semibold mb-3 text-gray-900">Follow Us</h4>
                  <div className="flex items-center space-x-3">
                    {contactInfo.social.map((social) => (
                      <a
                        key={social.icon}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-full bg-teal-600 hover:bg-teal-700 flex items-center justify-center text-white transition"
                      >
                        <i className={`${social.icon} text-sm`}></i>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>

    {/* Global Styles */}
    <style jsx global>{`
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .animate-fadeIn {
        animation: fadeIn 0.3s ease-out forwards;
      }
      .nav-link {
        position: relative;
        color: #2d3748;
        font-weight: 500;
        transition: color 0.2s;
      }
      .nav-link:hover {
        color: #2b6cb0;
      }
      .nav-link::after {
        content: "";
        position: absolute;
        bottom: -5px;
        left: 0;
        width: 0;
        height: 2px;
        background-color: #2b6cb0;
        transition: width 0.3s;
      }
      .nav-link:hover::after {
        width: 100%;
      }
    `}</style>
  </div>
);
};

export default Header;
